import { GroupPosition } from "./Cart";
import { KitchenProduct } from "./KitchenMenu";
import { OperatorAction } from "./Operator";
import { ProductPreset } from "./ProductPreset";
import { Wrap } from "./Wrap";
import { NewMessage } from "../components/state/Common";

export enum ActionKind {
    // Cart actions
    AddProduct = "addProduct",
    UpdateLineItem = "updateLineItem",
    DeleteLineItem = "deleteLineItem",
    DeleteCombo = "deleteCombo",
    ScanId = "scanId",
    ApplyCoupon = "applyCoupon",

    // Kitchen actions
    ShowKitchenProductSelector = "showKitchenProductSelector",
    HideKitchenProductSelector = "hideKitchenProductSelector",
    AddKitchenProduct = "addKitchenProduct",

    // Post checkout actions
    SendFeedback = "sendFeedback",
    StartGetReceipt = "startGetReceipt",
    PrintReceipt = "printReceipt",
    PrintKitchenReceipt = "printKitchenReceipt",

    // Operator actions
    BackToOperatorModeMenu = "backToOperatorModeMenu",
    ScanOperatorBadge = "scanOperatorBadge",
    ScanOperatorBadgeCartCheck = "scanOperatorBadgeCartCheck",
    ShowOrders = "showOrders",
    LoadMoreOrders = "loadMoreOrders",
    ShowInsights = "showInsights",
    ShowHardware = "showHardware",
    ShowSystemInformation = "showSystemInformation",
    ShowConfiguration = "showConfiguration",
    SetScoStatus = "setScoStatus",
    SetCartCheck = "setCartCheck",
    PaymentDeviceLogin = "paymentDeviceLogin",
    ProbePaymentTerminal = "probePaymentTerminal",
    UpdatePaymentDeviceIp = "updatePaymentDeviceIp",

    // Other actions
    Start = "start",
    ChangeLanguage = "changeLanguage",
    ShowProducts = "showProducts",
    HideProducts = "hideProducts",
    ShowKitchenMenu = "showKitchenMenu",
    HideKitchenMenu = "hideKitchenMenu",
    ShowCheckoutDialog = "showCheckoutDialog",
    HideCheckoutDialog = "hideCheckoutDialog",
    ConfirmUnappliedCoupon = "confirmUnappliedCoupon",
    StartPayment = "startPayment",
    HideCartErrorDialog = "hideCartErrorDialog",
    HideIdScan = "hideIdScan",
    FinalizeSession = "finalizeSession",
    Reset = "reset",
    ToggleAccessibilityMode = "toggleAccessibilityMode",
    Flap = "flap",
    ShowOperatorBadgeVerification = "showOperatorBadgeVerification",
    HideOperatorBadgeVerification = "hideOperatorBadgeVerification",
    TogglePaymentSimulator = "togglePaymentSimulator",
    ShowMessage = "showMessage"
}

export type ManuallyAddedProduct = {
    kind: "manuallyAdded";
    code: string;
    analyticsSource: "fixtasten" | "promo" | "quickAccess";
};

export type AddProduct = Wrap<
    ActionKind.AddProduct,
    {
        payload: ManuallyAddedProduct | ScannedCode;
    }
>;

type UpdateLineItem = Wrap<
    ActionKind.UpdateLineItem,
    { product: { id: string; action: "increment" | "decrement" } }
>;
type DeleteLineItem = Wrap<
    ActionKind.DeleteLineItem,
    { product: { id: string } }
>;
type DeleteCombo = Wrap<ActionKind.DeleteCombo, { position: GroupPosition }>;
type ScanId = Wrap<
    ActionKind.ScanId,
    {
        identityDocumentScan: ScannedCode;
        attemptedAction: ManuallyAddedProduct | ScannedCode;
    }
>;
export type ApplyCoupon = Wrap<
    ActionKind.ApplyCoupon,
    {
        code: string;
    }
>;

type CartAction =
    | AddProduct
    | UpdateLineItem
    | DeleteLineItem
    | ApplyCoupon
    | DeleteCombo
    | ScanId;

type ShowKitchenProductSelector = Wrap<
    ActionKind.ShowKitchenProductSelector,
    {
        product: KitchenProduct;
    }
>;
type HideKitchenProductSelector = Wrap<ActionKind.HideKitchenProductSelector>;
type AddKitchenProduct = Wrap<
    ActionKind.AddKitchenProduct,
    {
        productId: string;
        barcode: string;
        quantity: number;
        addOnOptionIds: string[];
        comboBarcodes: string[];
        upsellingProductBarcodes: string[];
    }
>;

type KitchenAction =
    | ShowKitchenProductSelector
    | HideKitchenProductSelector
    | AddKitchenProduct;

type SendFeedback = Wrap<ActionKind.SendFeedback, { rate: number }>;
type PrintReceipt = Wrap<
    ActionKind.PrintReceipt,
    { orderId: string; kitchenOrderId?: string }
>;
type PrintKitchenReceipt = Wrap<
    ActionKind.PrintKitchenReceipt,
    { kitchenOrderId: string }
>;
type StartGetReceipt = Wrap<ActionKind.StartGetReceipt, { orderId: string }>;

type PostCheckoutAction =
    | SendFeedback
    | StartGetReceipt
    | PrintReceipt
    | PrintKitchenReceipt;

export type ScannedCode = {
    kind: "scanned";
    code: string;
    device?: "pistol" | "stationary";
};

type Start = Wrap<
    ActionKind.Start,
    { scannedCode?: ScannedCode; openKitchen?: boolean }
>;
type ChangeLanguage = Wrap<ActionKind.ChangeLanguage, { code: string }>;
type ShowOperatorBadgeVerification =
    Wrap<ActionKind.ShowOperatorBadgeVerification>;
type HideOperatorBadgeVerification =
    Wrap<ActionKind.HideOperatorBadgeVerification>;
type ShowProducts = Wrap<
    ActionKind.ShowProducts,
    { productPreset: ProductPreset }
>;
type HideProducts = Wrap<ActionKind.HideProducts>;
type ShowKitchenMenu = Wrap<ActionKind.ShowKitchenMenu, { code?: string }>;
type HideKitchenMenu = Wrap<ActionKind.HideKitchenMenu>;
type ShowCheckoutDialog = Wrap<ActionKind.ShowCheckoutDialog>;
type ShowConfiguration = Wrap<ActionKind.ShowConfiguration>;
type HideCheckoutDialog = Wrap<ActionKind.HideCheckoutDialog>;
type ConfirmUnappliedCoupon = Wrap<ActionKind.ConfirmUnappliedCoupon>;
type StartPayment = Wrap<ActionKind.StartPayment>;
type HideCartErrorDialog = Wrap<ActionKind.HideCartErrorDialog>;
type HideIdScan = Wrap<ActionKind.HideIdScan>;
type FinalizeSession = Wrap<ActionKind.FinalizeSession>;
type Reset = Wrap<ActionKind.Reset>;
type ToggleAccessibilityMode = Wrap<ActionKind.ToggleAccessibilityMode>;
type Flap = Wrap<ActionKind.Flap>;
type TogglePaymentSimulator = Wrap<ActionKind.TogglePaymentSimulator>;
type ShowMessage = Wrap<
    ActionKind.ShowMessage,
    {
        message: NewMessage;
    }
>;
type SetScoStatus = Wrap<ActionKind.SetScoStatus, { isEnabled: boolean }>;
type SetCartCheck = Wrap<ActionKind.SetCartCheck, { isEnabled: boolean }>;

type ScanOperatorBadgeCartCheck = Wrap<ActionKind.ScanOperatorBadgeCartCheck>;

export type Action =
    | CartAction
    | KitchenAction
    | PostCheckoutAction
    | Start
    | ChangeLanguage
    | ShowProducts
    | HideProducts
    | ShowKitchenMenu
    | HideKitchenMenu
    | ShowCheckoutDialog
    | HideCheckoutDialog
    | ConfirmUnappliedCoupon
    | ShowConfiguration
    | StartPayment
    | HideCartErrorDialog
    | HideIdScan
    | FinalizeSession
    | Reset
    | ToggleAccessibilityMode
    | Flap
    | ShowOperatorBadgeVerification
    | HideOperatorBadgeVerification
    | OperatorAction
    | TogglePaymentSimulator
    | ShowMessage
    | SetScoStatus
    | SetCartCheck
    | ScanOperatorBadgeCartCheck;
